import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { vehiclesApi, type VehiclesParams } from "../api/vehicles.api";

export const vehicleQueryKeys = {
  all: ["vehicles"] as const,
  lists: () => [...vehicleQueryKeys.all, "list"] as const,
  list: (params: VehiclesParams) =>
    [...vehicleQueryKeys.lists(), params] as const,
  details: () => [...vehicleQueryKeys.all, "detail"] as const,
  detail: (id: string) => [...vehicleQueryKeys.details(), id] as const,
  pending: () => [...vehicleQueryKeys.all, "pending"] as const,
};

export function useVehicles(params: VehiclesParams) {
  return useQuery({
    queryKey: vehicleQueryKeys.list(params),
    queryFn: () => vehiclesApi.getAll(params),
    staleTime: 30 * 1000,
  });
}

export function usePendingVehicles() {
  return useQuery({
    queryKey: vehicleQueryKeys.pending(),
    queryFn: () => vehiclesApi.getPending(),
    staleTime: 30 * 1000,
  });
}

export function useVehicle(id: string) {
  return useQuery({
    queryKey: vehicleQueryKeys.detail(id),
    queryFn: () => vehiclesApi.getById(id),
    enabled: !!id,
  });
}

export function useApproveVehicle() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await vehiclesApi.approve(id);
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: vehicleQueryKeys.lists() });
      queryClient.invalidateQueries({ queryKey: vehicleQueryKeys.pending() });
      queryClient.invalidateQueries({ queryKey: vehicleQueryKeys.detail(id) });
      toast.success("Vehicle approved successfully");
    },
  });
}

export function useRejectVehicle() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason: string }) => {
      await vehiclesApi.reject(id, reason);
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: vehicleQueryKeys.lists() });
      queryClient.invalidateQueries({ queryKey: vehicleQueryKeys.pending() });
      queryClient.invalidateQueries({ queryKey: vehicleQueryKeys.detail(id) });
      toast.success("Vehicle rejected");
    },
  });
}
